import apiClient from '@/lib/api-client';
import { transformBookingNoteSummary } from './sessions.service';
import type {
  Booking,
  BookingFilters,
  Service,
  Provider,
  CustomQuestion
} from '@/types/booking';

class BookingService {
  // Admin: Get all bookings for the organization
  async getAdminBookings(filters?: BookingFilters & {
    page?: number;
    per_page?: number;
    time_range?: 'upcoming' | 'past' | 'all';
    search?: string;
    sort_dir?: 'asc' | 'desc';
    provider_id?: number;
  }): Promise<{
    bookings: Booking[];
    pagination: { page: number; total_pages: number; total_count: number; per_page: number };
    statusCounts: { all: number; pending: number; confirmed: number; completed: number; cancelled: number; no_show: number };
  }> {
    const { data } = await apiClient.get<{
      data: any[];
      meta?: {
        page: number;
        total_pages: number;
        total_count: number;
        per_page?: number;
        status_counts?: { all: number; pending: number; confirmed: number; completed: number; cancelled: number; no_show: number };
      };
    }>('/v1/admin/bookings', { params: filters });
    const bookings = data.data.map(booking => ({
      id: booking.id,
      publicId: booking.public_id,
      bookingCode: booking.booking_code,
      status: booking.status,
      customerId: booking.customer_id,
      customerFirstName: booking.customer_first_name,
      customerLastName: booking.customer_last_name,
      customerEmail: booking.customer_email,
      customerPhone: booking.customer_phone,
      notes: booking.notes,
      custom_answers: booking.custom_answers,
      availableSlotId: booking.available_slot?.id,
      organizationId: booking.organization_id,
      createdAt: booking.created_at,
      updatedAt: booking.updated_at,
      // Map direct start/end time (availability-based bookings)
      start_time: booking.start_time,
      end_time: booking.end_time,
      // Map direct service and provider fields (new slot system)
      service: booking.service ? {
        id: booking.service.id,
        name: booking.service.name,
        duration_minutes: booking.service.duration_minutes,
        durationMinutes: booking.service.duration_minutes,
        price: booking.service.price,
        max_capacity: booking.service.max_capacity,
        maxCapacity: booking.service.max_capacity,
        custom_booking_questions: booking.service.custom_booking_questions || [],
        customBookingQuestions: booking.service.custom_booking_questions || []
      } : undefined,
      provider: booking.provider ? {
        id: booking.provider.id,
        name: booking.provider.name,
        displayName: booking.provider.name,
        title: booking.provider.title
      } : undefined,
      // Keep available_slot for backward compatibility
      availableSlot: booking.available_slot ? {
        id: booking.available_slot.id,
        startTime: booking.available_slot.start_time,
        endTime: booking.available_slot.end_time,
        available: booking.available_slot.available ?? true,
        providerId: booking.available_slot.provider_id,
        serviceId: booking.available_slot.service_id,
        max_capacity: booking.available_slot.max_capacity,
        maxCapacity: booking.available_slot.max_capacity,
        current_bookings: booking.available_slot.current_bookings,
        currentBookings: booking.available_slot.current_bookings,
        organizationId: booking.available_slot.organization_id,
        createdAt: booking.available_slot.created_at,
        updatedAt: booking.available_slot.updated_at,
        service: booking.available_slot.service ? {
          id: booking.available_slot.service.id,
          name: booking.available_slot.service.name
        } : undefined,
        provider: booking.available_slot.provider ? {
          id: booking.available_slot.provider.id,
          displayName: booking.available_slot.provider.display_name || booking.available_slot.provider.name
        } : undefined
      } : undefined
    } as unknown as Booking));

    return {
      bookings,
      pagination: {
        page: data.meta?.page || 1,
        total_pages: data.meta?.total_pages || 1,
        total_count: data.meta?.total_count || bookings.length,
        per_page: data.meta?.per_page || bookings.length
      },
      statusCounts: data.meta?.status_counts || {
        all: 0, pending: 0, confirmed: 0, completed: 0, cancelled: 0, no_show: 0
      }
    };
  }

  // Admin: Get single booking by ID or public_id (UUID)
  async getAdminBooking(id: number | string): Promise<Booking> {
    const { data } = await apiClient.get<{ data: any }>(`/v1/admin/bookings/${id}`);
    const booking = data.data;

    // Transform response to match Booking type
    return {
      id: booking.id,
      publicId: booking.public_id,
      bookingCode: booking.booking_code,
      status: booking.status,
      customerId: booking.customer_id,
      customerFirstName: booking.customer_first_name,
      customerLastName: booking.customer_last_name,
      customerEmail: booking.customer_email,
      customerPhone: booking.customer_phone,
      notes: booking.notes,
      custom_answers: booking.custom_answers,
      sessionNote: transformBookingNoteSummary(booking.session_note),
      bookingSeriesId: booking.booking_series_id,
      seriesOccurrenceIndex: booking.series_occurrence_index,
      series: booking.series ? {
        id: booking.series.id,
        futureCount: booking.series.future_count,
        customerCount: booking.series.customer_count,
      } : undefined,
      availableSlotId: booking.available_slot?.id,
      organizationId: booking.organization_id,
      createdAt: booking.created_at,
      updatedAt: booking.updated_at,
      service: booking.service ? {
        id: booking.service.id,
        name: booking.service.name,
        duration_minutes: booking.service.duration_minutes,
        durationMinutes: booking.service.duration_minutes,
        price: booking.service.price,
        max_capacity: booking.service.max_capacity,
        maxCapacity: booking.service.max_capacity,
        custom_booking_questions: booking.service.custom_booking_questions || [],
        customBookingQuestions: booking.service.custom_booking_questions || []
      } : undefined,
      provider: booking.provider ? {
        id: booking.provider.id,
        name: booking.provider.name,
        displayName: booking.provider.name,
        title: booking.provider.title
      } : undefined,
      start_time: booking.start_time,
      end_time: booking.end_time,
      availableSlot: booking.available_slot ? {
        id: booking.available_slot.id,
        startTime: booking.available_slot.start_time,
        endTime: booking.available_slot.end_time,
        maxCapacity: booking.available_slot.max_capacity,
        currentBookings: booking.available_slot.current_bookings,
        available: booking.available_slot.available ?? true,
        providerId: booking.available_slot.provider?.id,
        serviceId: booking.available_slot.service?.id,
        service: booking.available_slot.service ? {
          id: booking.available_slot.service.id,
          name: booking.available_slot.service.name
        } : undefined,
        provider: booking.available_slot.provider ? {
          id: booking.available_slot.provider.id,
          displayName: booking.available_slot.provider.display_name || booking.available_slot.provider.name
        } : undefined
      } : undefined
    } as unknown as Booking;
  }

  async confirmBooking(id: number | string): Promise<Booking> {
    const { data } = await apiClient.post<{ data: Booking }>(`/v1/bookings/${id}/confirm`);
    return data.data;
  }

  async cancelBooking(id: number | string, reason?: string): Promise<Booking> {
    const { data } = await apiClient.post<{ data: Booking }>(`/v1/bookings/${id}/cancel`, { reason });
    return data.data;
  }

  // Admin batch ops on a group/recurring series. All act on future bookings.
  // When `customerEmail` is given, the op is scoped to that one participant's
  // bookings in the series; omit it to act on the whole series.
  async cancelSeries(
    seriesId: string,
    customerEmail?: string,
  ): Promise<{ series_id: string; cancelled_count: number }> {
    const { data } = await apiClient.delete<{ data: { series_id: string; cancelled_count: number } }>(
      `/v1/admin/bookings/series/${seriesId}`,
      customerEmail ? { params: { customer_email: customerEmail } } : undefined,
    );
    return data.data;
  }

  async updateSeries(
    seriesId: string,
    updates: { notes?: string; status?: 'confirmed'; customerEmail?: string },
  ): Promise<{ series_id: string; notes_updated_count: number; confirmed_count: number }> {
    const { notes, status, customerEmail } = updates;
    const { data } = await apiClient.patch<{
      data: { series_id: string; notes_updated_count: number; confirmed_count: number };
    }>(`/v1/admin/bookings/series/${seriesId}`, {
      notes,
      status,
      customer_email: customerEmail,
    });
    return data.data;
  }

  // Services
  async getServices(): Promise<Service[]> {
    const { data } = await apiClient.get<{ data: any[] }>('/v1/services');
    return data.data.map(service => ({
      id: service.id,
      name: service.name,
      description: service.description,
      duration: service.duration_minutes,
      duration_minutes: service.duration_minutes,
      price: parseFloat(service.price),
      active: service.active,
      hide_price: service.hide_price || false,
      max_capacity: service.max_capacity,
      group_booking: service.group_booking,
      waitlist_enabled: service.waitlist_enabled ?? false,
      organizationId: service.organizationId || 0,
      createdAt: service.createdAt || new Date().toISOString(),
      updatedAt: service.updatedAt || new Date().toISOString(),
      custom_booking_questions: service.custom_booking_questions || [],
      provider_ids: service.provider_ids || []
    }));
  }

  async getService(id: number): Promise<Service> {
    const { data } = await apiClient.get<{ data: Service }>(`/v1/services/${id}`);
    return data.data;
  }

  async createService(service: Omit<Service, 'id' | 'createdAt' | 'updatedAt'> & { provider_ids?: number[] }): Promise<Service> {
    // Transform duration to duration_minutes for backend compatibility
    const { provider_ids, ...rest } = service as any;
    const serviceData: any = {
      ...rest,
      duration_minutes: service.duration,
    };
    if (provider_ids) {
      serviceData.provider_ids = provider_ids;
    }
    const { data } = await apiClient.post<{ data: any }>('/v1/services', { service: serviceData });
    // Transform response back to frontend format
    const responseService = data.data;
    return {
      id: responseService.id,
      name: responseService.name,
      description: responseService.description,
      duration: responseService.duration_minutes,
      price: parseFloat(responseService.price),
      active: responseService.active,
      hide_price: responseService.hide_price || false,
      max_capacity: responseService.max_capacity,
      waitlist_enabled: responseService.waitlist_enabled ?? false,
      organizationId: responseService.organizationId || 0,
      createdAt: responseService.created_at || responseService.createdAt,
      updatedAt: responseService.updated_at || responseService.updatedAt,
      provider_ids: responseService.provider_ids || []
    };
  }

  async updateService(id: number, updates: Partial<Service> & { provider_ids?: number[] }): Promise<Service> {
    // Transform duration to duration_minutes if present
    const { provider_ids, ...rest } = updates as any;
    const updateData: any = { ...rest };
    if (updateData.duration !== undefined) {
      updateData.duration_minutes = updateData.duration;
      delete updateData.duration;
    }
    if (provider_ids !== undefined) {
      updateData.provider_ids = provider_ids;
    }
    const { data } = await apiClient.put<{ data: any }>(`/v1/services/${id}`, { service: updateData });
    // Transform response back to frontend format
    const responseService = data.data;
    return {
      id: responseService.id,
      name: responseService.name,
      description: responseService.description,
      duration: responseService.duration_minutes,
      price: parseFloat(responseService.price),
      active: responseService.active,
      hide_price: responseService.hide_price || false,
      max_capacity: responseService.max_capacity,
      waitlist_enabled: responseService.waitlist_enabled ?? false,
      organizationId: responseService.organizationId || 0,
      createdAt: responseService.created_at || responseService.createdAt,
      updatedAt: responseService.updated_at || responseService.updatedAt,
      provider_ids: responseService.provider_ids || []
    };
  }

  async deleteService(id: number): Promise<void> {
    await apiClient.delete(`/v1/services/${id}`);
  }

  async checkDurationImpact(id: number, durationMinutes: number): Promise<{
    future_unbooked: number;
    future_booked: number;
    past: number;
    can_update: boolean;
  }> {
    const { data } = await apiClient.get<{ data: any }>(`/v1/services/${id}/check_duration_impact`, {
      params: { duration_minutes: durationMinutes }
    });
    return data.data;
  }

  async updateServiceCustomQuestions(serviceId: number, customQuestions: CustomQuestion[]): Promise<{ custom_booking_questions: CustomQuestion[]; message: string }> {
    const { data } = await apiClient.put<{ data: { custom_booking_questions: CustomQuestion[]; message: string } }>(
      `/v1/services/${serviceId}/custom_questions`,
      { custom_booking_questions: customQuestions }
    );
    return data.data;
  }

  // Providers
  async getProviders(): Promise<Provider[]> {
    const { data } = await apiClient.get<{ data: any[] }>('/v1/providers');
    return data.data.map(provider => ({
      id: provider.id,
      userId: provider.userId || 0,
      displayName: provider.name || provider.displayName,
      email: provider.email,
      phone: provider.phone,
      specialization: provider.specialization,
      bio: provider.bio,
      active: provider.active,
      organizationId: provider.organizationId || 0,
      createdAt: provider.createdAt || new Date().toISOString(),
      updatedAt: provider.updatedAt || new Date().toISOString(),
      services: provider.services
    }));
  }

  async getProvider(id: number): Promise<Provider> {
    const { data } = await apiClient.get<{ data: Provider }>(`/v1/providers/${id}`);
    return data.data;
  }

  async updateProvider(id: number, updates: Partial<Provider>): Promise<Provider> {
    // Transform camelCase to snake_case for backend
    const updateData: any = {};

    if (updates.displayName !== undefined) updateData.displayName = updates.displayName;
    if (updates.email !== undefined) updateData.email = updates.email;
    if (updates.phone !== undefined) updateData.phone = updates.phone;
    if (updates.bio !== undefined) updateData.bio = updates.bio;
    if (updates.specialization !== undefined) updateData.specialization = updates.specialization;
    if (updates.active !== undefined) updateData.active = updates.active;

    // Handle service IDs if present (extract from services array if needed)
    if (updates.serviceIds !== undefined) {
      updateData.serviceIds = updates.serviceIds;
    } else if (updates.services !== undefined) {
      updateData.serviceIds = updates.services.map(s => s.id);
    }

    // Send data directly (Rails controller handles both root and nested params)
    const { data } = await apiClient.put<{ data: any }>(`/v1/providers/${id}`, updateData);

    // Transform response back to frontend format
    const responseProvider = data.data;
    return {
      id: responseProvider.id,
      userId: responseProvider.user_id || 0,
      displayName: responseProvider.display_name || responseProvider.name,
      email: responseProvider.email,
      phone: responseProvider.phone,
      bio: responseProvider.bio,
      specialization: responseProvider.specialization,
      active: responseProvider.active,
      organizationId: responseProvider.organization_id || 0,
      createdAt: responseProvider.created_at || responseProvider.createdAt,
      updatedAt: responseProvider.updated_at || responseProvider.updatedAt,
      services: responseProvider.services
    };
  }
}

export const bookingService = new BookingService();